import { withTranslation } from "next-i18next";
import { useState, useRef } from "react";
import { useRouter } from "next/compat/router";
import { useFormatDate } from "@hooks/use-format-date";
import Button from "@components/core/Button";
import Offcanvas from "@components/core/Offcanvas";
import MenuListDropdown from "@components/core/MenuListDropdown";
import UserListForm from "@components/core/UserListForm";
import MenuUserListEdit from "./MenuUserListEdit";

function UserListHeader({ fetchUserList, userList, t }) {
  const router = useRouter();
  const formatDate = useFormatDate();
  const {
    editable,
    creator_first_name: ownerName,
    created_at: createdAt,
    name,
    uuid,
  } = userList || {};

  const [dropdownMenuUserListOpened, setDropdownMenuUserListOpened] = useState(false);
  const [offcanvasMenuUserListOpened, setOffcanvasMenuUserListOpened] = useState(false);
  const [offcanvasEditUserListOpened, setOffcanvasEditUserListOpened] = useState(false);

  const menuListDropdownRef = useRef();

  function openDropdownMenuUserList() {
    setDropdownMenuUserListOpened(true);
    addMenuListDropdownEventListener();
  }

  function closeDropdownMenuUserList() {
    setDropdownMenuUserListOpened(false);
  }

  function addMenuListDropdownEventListener() {
    const handleClickOutside = (event) => {
      if (menuListDropdownRef?.current && !menuListDropdownRef?.current?.contains(event.target)) {
        closeDropdownMenuUserList();
        document.removeEventListener("mousedown", handleClickOutside);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
  }

  function openOffcanvasMenuUserList() {
    setOffcanvasMenuUserListOpened(true);
  }

  function closeOffcanvasMenuUserList() {
    setOffcanvasMenuUserListOpened(false);
  }

  function openOffcanvasEditUserList() {
    setOffcanvasEditUserListOpened(true);
    closeDropdownMenuUserList();
    closeOffcanvasMenuUserList();
  }

  function closeOffcanvasEditUserList() {
    setOffcanvasEditUserListOpened(false);
  }

  function closeOffcanvasEditUserListAndReload() {
    fetchUserList();
    closeOffcanvasEditUserList();
  }

  const userListLink = `${process.env.NEXT_PUBLIC_CUSTOMER_WEBSITE}${router.asPath}`;
  const shareData = {
    title: t("catalog:userlist.share_title", { name }),
    text: t("catalog:userlist.share_content", { link: userListLink }),
  };

  function copyToClipboard() {
    navigator.clipboard.writeText(userListLink);
  }

  async function shareUserList() {
    try {
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        throw new Error("can't use navigator.share API");
      }
    } catch (err) {
      copyToClipboard();
    }
  }

  return (
    <div className="container-lg p-0 mt-5">
      <p className="mb-0 font-size-xs font-weight-bold text-center text-gray text-uppercase">
        {editable
          ? t("catalog:userlist.title_owner")
          : t("catalog:userlist.title_guest", { ownerName })}
      </p>

      <span className="d-flex justify-content-center align-items-center mb-2 px-3">
        <span className="d-inline-block text-center">
          <span className="text-center mb-0 h3">{name}</span>
          {editable && (
            <>
              <Button
                variant="link-primary no-underline ms-1 mb-1"
                size="sm"
                className="d-inline-block d-md-none"
                onClick={openOffcanvasMenuUserList}
                leftIconName="pen"
              />
              <div className="d-inline-block position-relative">
                <Button
                  variant="link-primary no-underline ms-1 mb-1"
                  size="sm"
                  className="d-none d-md-block"
                  onClick={openDropdownMenuUserList}
                  leftIconName="pen"
                />
                <MenuListDropdown
                  ref={menuListDropdownRef}
                  show={dropdownMenuUserListOpened}
                  className="pt-1 userlist-menu-list-dropdown"
                >
                  <MenuUserListEdit
                    openOffcanvasEditUserList={openOffcanvasEditUserList}
                    userListUuid={uuid}
                  />
                </MenuListDropdown>
              </div>
            </>
          )}
        </span>
      </span>

      <div className="d-flex justify-content-center align-items-center">
        <p className="mb-0">{t("catalog:userlist.created_at", { date: formatDate(createdAt) })}</p>
        <p className="mb-0 mx-1">|</p>
        <Button
          trackingName="ShareUserlist"
          variant="link-primary no-underline"
          onClick={shareUserList}
        >
          {t("catalog:userlist.share")}
        </Button>
      </div>

      <Offcanvas
        show={offcanvasMenuUserListOpened}
        onHide={closeOffcanvasMenuUserList}
        placement="bottom"
        title={t("catalog:userlist.edit")}
      >
        <div className="pb-5">
          <MenuUserListEdit
            openOffcanvasEditUserList={openOffcanvasEditUserList}
            userListUuid={uuid}
          />
        </div>
      </Offcanvas>

      <Offcanvas
        show={offcanvasEditUserListOpened}
        onHide={closeOffcanvasEditUserList}
        placement="bottom"
        title={t("common:userlist.my_lists")}
        size="lg"
      >
        <div className="d-flex flex-grow-1 justify-content-center align-items-center">
          <div className="userlist-form-offcanvas-content">
            <UserListForm
              initialUserList={userList}
              onAfterSubmit={closeOffcanvasEditUserListAndReload}
            />
          </div>
        </div>
      </Offcanvas>
    </div>
  );
}

export default withTranslation("catalog")(UserListHeader);
